/**
 * If this agent were compromised, what would it influence.
 *
 * Exposure's depth on an object Core already shows. The card says what the
 * agent is; this says what falls with it.
 *
 * Why it is not a degree
 * ---------------------
 * Three implementations in this product answered a version of this question
 * with a COUNT OF EDGES — `reach_query.blast_radius` looks one hop,
 * `mesh._apply_blast_radius_overlay` takes max(in,out) plus the tier on one
 * adjacent edge, `main.compute_blast_radius` buckets tool names by regex. An
 * agent adjacent to one store that feeds four others has out-degree one and
 * touches five things. `counterfactual.assess` walks the graph instead, which
 * is why this component reports a set and how far it travelled rather than a
 * number next to a dial.
 *
 * Absence is not zero
 * -------------------
 * An agent missing from the map is NOT an agent that influences nothing. The
 * endpoint reads the latest scan per repository and `agent_id_hint` embeds the
 * definition's line number, so an older analysis's agents — or one whose
 * definition moved a line — will not be found. That renders as "not computed",
 * never as a clean result. An agent that genuinely reaches nothing is present
 * in the map with `component_count: 0`, and says so.
 */
import { Box, Chip, Tooltip, Typography } from '@mui/material'
import { HelpOutline } from '@mui/icons-material'
import { CounterfactualAgent } from '../services/api'
import BundleLocked from './BundleLocked'

interface AgentBlastRadiusProps {
  /** `null` means the hint was not in the map — not computed for this agent.
   *  Distinct from an entry whose `component_count` is 0. */
  exposure: CounterfactualAgent | null
  /** True while the shared fetch is in flight; renders nothing rather than a
   *  spinner per card. */
  loading?: boolean
  /** The route refused for want of ARIAS Exposure. Distinct from `exposure`
   *  being null, which means the agent was not in a result we DID get — and
   *  which used to be explained with a tooltip about scan recency and line
   *  numbers even when the real cause was an entitlement. */
  notEntitled?: boolean
}

const AgentBlastRadius = ({ exposure, loading, notEntitled }: AgentBlastRadiusProps) => {
  if (loading) return null

  // Checked BEFORE the null branch below, which would otherwise explain an
  // entitlement refusal as a scan-recency problem.
  if (notEntitled) {
    return (
      <BundleLocked feature="attack_paths" label="Blast radius" />
    )
  }

  if (!exposure) {
    return (
      <Tooltip title="Blast radius is computed from the latest scan of each repository, and an agent is matched by a key that includes its definition's line number. This agent was not in that result — which is not a statement that it influences nothing.">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
          <HelpOutline sx={{ fontSize: 13, color: 'text.disabled' }} />
          <Typography variant="caption" color="text.disabled">
            blast radius not computed
          </Typography>
        </Box>
      </Tooltip>
    )
  }

  if (exposure.component_count === 0) {
    return (
      <Tooltip title="This agent reaches no knowledge-layer component we can see, so there is nothing for a compromise of it to spread to through the component graph. It may still hold tools.">
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
          reaches no components
        </Typography>
      </Tooltip>
    )
  }

  const { component_count, classified_count, max_hops, confidence, kinds } = exposure

  return (
    <Box sx={{ mt: 0.75 }}>
      <Tooltip
        title={
          `If compromised, this agent could influence ${component_count} component` +
          `${component_count === 1 ? '' : 's'}: everything it reaches, plus everything ` +
          `filled from those. ${classified_count > 0
            ? `${classified_count} carr${classified_count === 1 ? 'ies' : 'y'} a classification.`
            : 'None carries a classification we can see.'}` +
          `${max_hops > 0 ? ` Travels up to ${max_hops} derivation hop${max_hops === 1 ? '' : 's'} from what it touches.` : ''}`
        }
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            if compromised:
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            // Coloured only where something classified is in reach. A count
            // alone is breadth, not consequence, and colouring breadth would
            // teach a reader to ignore the colour.
            color={classified_count > 0 ? 'error' : 'default'}
            label={
              `${component_count} component${component_count === 1 ? '' : 's'}` +
              (classified_count > 0 ? ` · ${classified_count} classified` : '')
            }
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
          {max_hops > 0 && (
            <Typography variant="caption" color="text.secondary">
              {max_hops} hop{max_hops === 1 ? '' : 's'}
            </Typography>
          )}
          {/* An influence set resting on an inferred derivation is worth acting
              on and must not be presented as observed. */}
          {confidence === 'inferred' && (
            <Tooltip title="At least one step out from this agent was inferred rather than observed.">
              <Typography variant="caption" color="text.disabled">
                inferred
              </Typography>
            </Tooltip>
          )}
        </Box>
      </Tooltip>
      {kinds.length > 0 && (
        <Typography variant="caption" color="text.disabled" display="block">
          {kinds.map((k) => k.replace(/_/g, ' ')).join(', ')}
        </Typography>
      )}
    </Box>
  )
}

export default AgentBlastRadius
